import React, { useState } from 'react';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { BREEDS, BUSINESS_INFO } from '../data/mock';
import { analyticsService } from '../services/analyticsApi';
import { MessageCircle, Weight, Palette, Heart, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';

export const PuppyCard = ({ puppy, onEdit, isAdmin }) => {
  const [showSpecialModal, setShowSpecialModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const breed = BREEDS.find(b => b.id === puppy.breed);
  
  const images = puppy.images?.length ? puppy.images : (puppy.image ? [puppy.image] : []);
  
  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };
  
  const handleWhatsAppClick = () => {
    analyticsService.recordWhatsAppClick(puppy.id, puppy.name, puppy.breed);
    const message = encodeURIComponent(
      `¡Hola! Me interesa el cachorro "${puppy.name}" (${breed?.name}) que vi en su catálogo. ¿Podría darme más información?`
    );
    window.open(`https://wa.me/34${BUSINESS_INFO.whatsapp}?text=${message}`, '_blank');
  };

  const calculateAge = (birthDate) => {
    const birth = new Date(birthDate);
    const now = new Date();
    const months = Math.floor((now - birth) / (1000 * 60 * 60 * 24 * 30));
    if (months < 1) return 'Recién nacido';
    if (months === 1) return '1 mes';
    return `${months} meses`;
  };

  return (
    <>
      <Card data-testid={`puppy-card-${puppy.id}`} className="group overflow-hidden bg-white border-amber-100 hover:shadow-xl transition-all duration-500 hover:-translate-y-1">
        <div className="relative overflow-hidden">
          <img
            src={images[currentImageIndex] || 'https://via.placeholder.com/400x300?text=Sin+foto'}
            alt={puppy.name}
            className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-110"
          />
          
          {/* Image navigation */}
          {images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-1 rounded-full shadow-md transition-all"
              >
                <ChevronLeft className="w-5 h-5 text-amber-800" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-1 rounded-full shadow-md transition-all"
              >
                <ChevronRight className="w-5 h-5 text-amber-800" />
              </button>
              
              {/* Image dots */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                {images.map((imgUrl, idx) => (
                  <span
                    key={`dot-${idx}-${imgUrl.slice(-12)}`}
                    className={`w-2 h-2 rounded-full transition-all ${
                      idx === currentImageIndex ? 'bg-amber-500' : 'bg-white/60'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
          
          <div className="absolute top-3 right-3">
            <Badge 
              className={`${
                puppy.status === 'disponible' 
                  ? 'bg-emerald-500 hover:bg-emerald-600' 
                  : 'bg-amber-500 hover:bg-amber-600'
              } text-white font-semibold px-3 py-1`}
            >
              {puppy.status === 'disponible' ? 'Disponible' : 'Reservado'}
            </Badge>
          </div>
          <div className="absolute top-3 left-3">
            <Badge className="bg-white/90 text-amber-800 font-medium">
              {breed?.icon} {breed?.name}
            </Badge>
          </div>
        </div>
        
        <CardContent className="p-5">
          {/* Header: Name and Age */}
          <div className="mb-3">
            <h3 className="text-xl font-bold text-amber-900">{puppy.name}</h3>
            <p className="text-amber-600 text-sm">{calculateAge(puppy.birthDate)}</p>
          </div>

          {/* Special Conditions Section - Premium Style */}
          {puppy.hasSpecialConditions ? (
            <div 
              onClick={() => setShowSpecialModal(true)}
              className="mb-4 p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200 cursor-pointer hover:shadow-md transition-all duration-300"
            >
              {/* Badge */}
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-amber-600" />
                <span className="text-xs font-semibold uppercase tracking-wider text-amber-700">
                  Selección con condiciones especiales
                </span>
              </div>
              
              {/* Discount display */}
              {puppy.hasDiscount && puppy.previousPrice && (
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm" style={{ color: '#999' }}>Valor anterior</span>
                  <span className="text-sm font-medium" style={{ color: '#999' }}>{puppy.previousPrice}€</span>
                </div>
              )}

              {/* Prices */}
              <div className="space-y-1">
                {puppy.pricePedigree ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-amber-600 font-medium uppercase tracking-wide">Sin pedigree</span>
                      <span className="text-xl font-bold text-amber-800">{puppy.price}€</span>
                    </div>
                    <div className="flex items-center justify-between bg-white/60 rounded-lg px-3 py-1.5">
                      <span className="text-xs text-amber-700 font-medium uppercase tracking-wide">Con pedigree</span>
                      <span className="text-lg font-bold text-amber-800">{puppy.pricePedigree}€</span>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-amber-900">Precio</span>
                    <span className="text-2xl font-bold text-amber-800">{puppy.price}€</span>
                  </div>
                )}
              </div>
              
              {/* Subtle hint to click */}
              <p className="text-xs text-amber-600 mt-3 text-center opacity-70">
                Toca para más información
              </p>
            </div>
          ) : (
            /* Regular Price Display */
            <div className="mb-3">
              {puppy.priceType === 'consult' ? (
                <p className="text-xl font-bold text-amber-700 text-right">Consultar</p>
              ) : puppy.priceType === 'from' ? (
                <p className="text-xl font-bold text-amber-700 text-right">Desde {puppy.price}€</p>
              ) : puppy.pricePedigree ? (
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-amber-600 font-medium uppercase tracking-wide">Sin pedigree</span>
                    <span className="text-xl font-bold text-amber-700">{puppy.price}€</span>
                  </div>
                  <div className="flex items-center justify-between bg-amber-50 rounded-lg px-3 py-1.5">
                    <span className="text-xs text-amber-700 font-medium uppercase tracking-wide">Con pedigree</span>
                    <span className="text-lg font-bold text-amber-800">{puppy.pricePedigree}€</span>
                  </div>
                </div>
              ) : (
                <p className="text-2xl font-bold text-amber-700 text-right">{puppy.price}€</p>
              )}
            </div>
          )}
          
          <p className="text-gray-600 text-sm mb-4 line-clamp-2">{puppy.description}</p>
          
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="flex items-center gap-2 text-sm text-gray-700 bg-amber-50 rounded-lg px-3 py-2">
              <Heart className="w-4 h-4 text-amber-600" />
              <span>{puppy.sex}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-700 bg-amber-50 rounded-lg px-3 py-2">
              <Palette className="w-4 h-4 text-amber-600" />
              <span>{puppy.color}</span>
            </div>
            <div className="col-span-2 flex items-center gap-2 text-sm text-gray-700 bg-amber-50 rounded-lg px-3 py-2">
              <Weight className="w-4 h-4 text-amber-600" />
              <span>Peso adulto: {puppy.adultWeight}</span>
            </div>
          </div>
          
          {isAdmin ? (
            <button
              data-testid={`edit-puppy-${puppy.id}`}
              onClick={() => onEdit(puppy)}
              className="w-full py-3 bg-amber-100 text-amber-800 rounded-xl font-semibold hover:bg-amber-200 transition-all duration-300"
            >
              Editar Cachorro
            </button>
          ) : (
            <button
              data-testid={`whatsapp-puppy-${puppy.id}`}
              onClick={handleWhatsAppClick}
              disabled={puppy.status === 'reservado'}
              className={`w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 ${
                puppy.status === 'reservado'
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-emerald-500 text-white hover:bg-emerald-600 hover:shadow-lg'
              }`}
            >
              <MessageCircle className="w-5 h-5" />
              {puppy.status === 'reservado' ? 'Reservado' : 'Consultar por WhatsApp'}
            </button>
          )}
        </CardContent>
      </Card>

      {/* Special Conditions Modal - Premium Style */}
      <Dialog open={showSpecialModal} onOpenChange={setShowSpecialModal}>
        <DialogContent className="max-w-md bg-white border-0 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-center">
              <div className="flex items-center justify-center gap-2 text-amber-800">
                <Sparkles className="w-5 h-5 text-amber-600" />
                <span className="text-lg font-semibold tracking-wide">Ajuste de valor</span>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-5 mt-4">
            {/* Elegant Price Display */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6">
              {puppy.hasDiscount && puppy.previousPrice && (
                <div className="mb-4 text-center pb-3 border-b border-amber-200">
                  <span className="text-sm uppercase tracking-wider" style={{ color: '#999' }}>Valor anterior</span>
                  <p className="text-xl font-medium mt-1" style={{ color: '#999' }}>{puppy.previousPrice}€</p>
                </div>
              )}
              
              {puppy.pricePedigree ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm uppercase tracking-wider text-amber-700">Sin pedigree</span>
                    <p className="text-2xl font-bold text-amber-800">{puppy.price}€</p>
                  </div>
                  <div className="flex items-center justify-between bg-white/60 rounded-lg px-4 py-2">
                    <span className="text-sm uppercase tracking-wider text-amber-700">Con pedigree</span>
                    <p className="text-xl font-bold text-amber-800">{puppy.pricePedigree}€</p>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <span className="text-sm uppercase tracking-wider text-amber-700">Precio</span>
                  <p className="text-3xl font-bold text-amber-800 mt-1">{puppy.price}€</p>
                </div>
              )}
            </div>
            
            {/* Explanation text - Elegant */}
            <div className="px-2">
              <p className="text-gray-600 text-sm leading-relaxed text-center italic">
                "{puppy.specialConditionsText || 'Hemos realizado un ajuste de valor para facilitar su incorporación a una familia adecuada, manteniendo intactos los estándares, cuidados y dedicación de nuestra línea.'}"
              </p>
            </div>
            
            {/* WhatsApp button - Premium */}
            <button
              onClick={() => {
                setShowSpecialModal(false);
                handleWhatsAppClick();
              }}
              className="w-full py-4 bg-amber-700 text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-amber-800 transition-all duration-300 shadow-lg"
            >
              <MessageCircle className="w-5 h-5" />
              Solicitar información
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
